/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { createRouter, createWebHistory } from 'vue-router'

import ReactAppRedirect from '@/components/ReactAppRedirect.vue'
import AppLayout from '@/layouts/AppLayout.vue'
import PageNotFound from '@/pages/PageNotFound.vue'
import SentryTest from '@/pages/SentryTest.vue'
import { authService } from '@/services/authService'
import { useAuthStore } from '@/store/authStore'
import { useGlobalStore } from '@/store/globalStore'

import { authRouter } from './authRouter'
import { dashboardRouter } from './dashboardRouter'
import { dietsRouter } from './dietsRouter'
import { dishesRouter } from './dishesRouter'
import { ingredientsRouter } from './ingredientsRouter'

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    authRouter,
    dashboardRouter,
    dietsRouter,
    ingredientsRouter,
    dishesRouter,
    {
      path: '/',
      redirect: { name: 'DashboardBase' },
    },
    {
      component: SentryTest,
      meta: {
        title: 'Sentry Test',
      },
      name: 'SentryTest',
      path: '/sentry/test/',
    },
    ...(import.meta.env.DEV
      ? [
          {
            component: () => import('@/pages/UiCatalog.vue'),
            meta: { title: 'UI catalog' },
            name: 'UiCatalog',
            path: '/__ui',
          },
        ]
      : []),
    {
      children: [
        {
          component: ReactAppRedirect,
          name: 'Visits',
          path: '',
        },
        {
          component: ReactAppRedirect,
          meta: {
            tab: 'upcoming',
            title: 'Visits - Upcoming',
          },
          name: 'VisitsUpcoming',
          path: 'upcoming',
        },
        {
          component: ReactAppRedirect,
          meta: {
            tab: 'inquiries',
            title: 'Visits - Inquiries',
          },
          name: 'VisitsInquiries',
          path: 'inquiries',
        },
      ],
      component: AppLayout,
      meta: {
        auth: true,
      },
      path: '/visits',
    },
    {
      children: [
        {
          component: ReactAppRedirect,
          meta: {
            title: 'Payments Form',
          },
          name: 'PaymentsForm',
          path: '',
        },
      ],
      component: AppLayout,
      meta: {
        auth: true,
      },
      path: '/payments-form',
    },
    {
      children: [
        {
          component: ReactAppRedirect,
          meta: {
            title: 'Chat',
          },
          name: 'Chat',
          path: '',
        },
      ],
      component: AppLayout,
      meta: {
        auth: true,
      },
      path: '/chat',
    },
    {
      children: [
        {
          component: ReactAppRedirect,
          meta: {
            title: 'Pacjenci',
          },
          name: 'PatientsList',
          path: '',
        },
        {
          children: [
            {
              path: '',
              redirect: { name: 'PatientDetailsMealPlans' },
            },
            {
              component: ReactAppRedirect,
              meta: {
                tab: 'dietary-guidelines',
                title: 'Pacjenci - Założenia diety',
              },
              name: 'PatientDetailsDietaryGuidelines',
              path: 'dietary-guidelines',
            },
            {
              component: ReactAppRedirect,
              meta: {
                tab: 'measurements',
                title: 'Pacjenci - Pomiary',
              },
              name: 'PatientDetailsMeasurements',
              path: 'measurements',
            },
            {
              component: ReactAppRedirect,
              meta: {
                tab: 'meal-plans',
                title: 'Pacjenci - Jadłospisy',
              },
              name: 'PatientDetailsMealPlans',
              path: 'meal-plans',
            },
            {
              component: ReactAppRedirect,
              meta: {
                tab: 'surveys',
                title: 'Pacjenci - Ankiety',
              },
              name: 'PatientDetailsSurveys',
              path: 'surveys',
            },
            {
              component: ReactAppRedirect,
              meta: {
                tab: 'settings',
                title: 'Pacjenci - Ustawienia',
              },
              name: 'PatientDetailsSettings',
              path: 'settings',
            },
          ],
          component: ReactAppRedirect,
          meta: {
            title: 'Pacjenci',
          },
          name: 'PatientDetails',
          path: ':patientId(\\d+)',
        },
      ],
      component: AppLayout,
      meta: {
        auth: true,
      },
      path: '/patients',
    },
    {
      children: [
        {
          name: 'Profile',
          path: '',
          redirect: { name: 'ProfileSettings' },
        },
        {
          component: ReactAppRedirect,
          meta: {
            tab: 'settings',
            title: 'Profile - Settings',
          },
          name: 'ProfileSettings',
          path: 'settings',
        },
        {
          component: ReactAppRedirect,
          meta: {
            tab: 'grow',
            title: 'Profile - Grow',
          },
          name: 'ProfileGrow',
          path: 'grow',
        },
        {
          component: ReactAppRedirect,
          meta: {
            tab: 'about',
            title: 'Profile - About Me',
          },
          name: 'ProfileAbout',
          path: 'about',
        },
        {
          component: ReactAppRedirect,
          meta: {
            tab: 'specs',
            title: 'Profile - Specializations',
          },
          name: 'ProfileSpecs',
          path: 'specs',
        },
        {
          component: ReactAppRedirect,
          meta: {
            tab: 'pricing',
            title: 'Profile - Pricing',
          },
          name: 'ProfilePricing',
          path: 'pricing',
        },
        {
          component: ReactAppRedirect,
          meta: {
            tab: 'education',
            title: 'Profile - Education',
          },
          name: 'ProfileEducation',
          path: 'education',
        },
        {
          component: ReactAppRedirect,
          meta: {
            tab: 'certs',
            title: 'Profile - Certificates',
          },
          name: 'ProfileCerts',
          path: 'certs',
        },
        {
          component: ReactAppRedirect,
          meta: {
            tab: 'booking',
            title: 'Profile - Booking',
          },
          name: 'ProfileBooking',
          path: 'booking',
        },
      ],
      component: AppLayout,
      meta: {
        auth: true,
      },
      path: '/profile',
    },
    {
      component: PageNotFound,
      meta: {
        title: 'Nie znaleziono strony',
      },
      name: 'PageNotFound',
      path: '/404',
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: { name: 'PageNotFound' },
    },
  ],
  scrollBehavior(_, __, savedPosition) {
    const position = savedPosition ?? { left: 0, top: 0 }
    document.querySelector<HTMLElement>('.app-content')?.scrollTo(position)
    return position
  },
})

router.beforeEach(async (to, _, next) => {
  const store = useGlobalStore()
  store.deleteMessages()
  if (to.matched.some(route => route.meta.auth)) {
    try {
      const authStore = useAuthStore()
      await authStore.verifyAuth()
      const permissions = store.user?.permissions ?? []

      if (
        permissions.includes('accounts.kcalmar_plan') ||
        to.path.includes('/profile') ||
        to.path.includes('/visits')
      ) {
        next()
      } else {
        window.location.assign(import.meta.env.VUE_APP_HOST + '/dietetyk/account/payments/')
      }
    } catch {
      authService.redirectToLoginPage(to.fullPath)
    }
  } else {
    next()
  }
})
